// Setup › What it knows › Pages it reads: the pages the notes come from, one compact line each. The
// shop's own page is the source of truth; a note is a checked extract of it. Pages change only by
// asking Chat (no panel here), and Chat tells the person when one has changed.
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy, { type StorefrontCopyKey } from '@/i18n/ns/storefront';
import { StatusPill, type StatusTone } from '@/components/ui/kit';
import { WorkspaceIcon } from '../icons';
import type { InformationPage, StoreInformation } from './api';
import { when } from './format';

type Copy = Readonly<Record<StorefrontCopyKey, string>>;

const TONE: Record<InformationPage['status'], StatusTone> = {
  ok: 'success',
  changed: 'warning',
  unreadable: 'danger',
  unchecked: 'neutral',
};

const STATUS_COPY: Record<InformationPage['status'], StorefrontCopyKey> = {
  ok: 'pageStatusOk',
  changed: 'pageStatusChanged',
  unreadable: 'pageStatusUnreadable',
  unchecked: 'pageStatusUnchecked',
};

/** "help.shop.com/en/faq": a page's host and path, as the notes' sources show it. */
export function pageLabel(address: string): string | null {
  try {
    const url = new URL(address);
    return `${url.host}${url.pathname === '/' ? '' : url.pathname}`;
  } catch {
    return null;
  }
}

// How the page came to be on the list: the member named it, a note names it, a page linked to it, or it
// was found when the shop was read.
function originLabel(page: InformationPage, from: string | null, copy: Copy): string {
  if (page.origin === 'member') return copy.pageAddedByYou;
  if (page.origin === 'note') return copy.pageFromNote;
  return from ? formatCopy(copy.pageLinkedFrom, { page: from }) : copy.pageFound;
}

// How the page came to be on the list, then when it was last checked and how many notes come from it.
function details(page: InformationPage, copy: Copy, locale: string): string {
  const from = page.foundOn ? pageLabel(page.foundOn) : null;
  return [
    originLabel(page, from, copy),
    page.checkedAt ? formatCopy(copy.marketChecked, { when: when(page.checkedAt, locale) }) : null,
    page.notes === 1 ? copy.pageNoteOne : null,
    page.notes > 1 ? formatCopy(copy.pageNotesMany, { count: String(page.notes) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function InformationPages({
  information,
  onChat,
  onUpdate,
}: {
  information: StoreInformation;
  onChat?: () => void;
  onUpdate?: () => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const locale = useAppCatalogLocale() ?? 'en';
  const { pages, attention } = information;
  // When something changed the useful next step is making the notes again; otherwise, naming pages.
  const needsUpdate = attention.changed + attention.unreadable > 0 && onUpdate;
  const action = needsUpdate ? onUpdate : onChat;
  return (
    <div className="sf-pages" data-testid="sf-pages">
      <div className="sf-knows-head sf-pages-head">
        <div>
          <h3>{copy.pagesTitle}</h3>
          <p>{copy.pagesBody}</p>
        </div>
        {action && (
          <button
            type="button"
            className="ws-button"
            data-testid={needsUpdate ? 'sf-pages-update' : 'sf-pages-chat'}
            onClick={action}
          >
            <WorkspaceIcon name="chat" size={14} />
            {needsUpdate ? copy.pagesUpdateWithChat : copy.pagesChangeWithChat}
          </button>
        )}
      </div>
      {pages.length ? (
        <ul className="sf-market-list" data-testid="sf-pages-list">
          {pages.map((page) => (
            <li
              key={page.url}
              className="sf-info-page"
              data-status={page.status}
              data-testid="sf-info-page"
            >
              <span className="sf-market-text">
                <b>
                  <a href={page.url} target="_blank" rel="noopener noreferrer">
                    {pageLabel(page.url) ?? page.url}
                  </a>
                </b>
                <small>{details(page, copy, locale)}</small>
              </span>
              <StatusPill tone={TONE[page.status]}>{copy[STATUS_COPY[page.status]]}</StatusPill>
            </li>
          ))}
        </ul>
      ) : (
        <p className="sf-knows-empty">{copy.pagesEmpty}</p>
      )}
    </div>
  );
}
